import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, CheckCircle, Loader2, Building2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { WizardStepper } from '@/components/custom/WizardStepper';
import { AddressPickerTable } from '@/components/custom/AddressPickerTable';
import { createTicketWithExtras, fetchDmpOrder, fetchDmpAddress, searchDmpAddresses, fetchDmpAddressByUtac, checkEligibility, searchSplitters, fetchDocumentedSplitters, searchSwapSplitters } from '@/hooks/useTickets';
import { AttachmentPicker } from '@/components/tickets/AttachmentPicker';
import { SimilarTicketsBanner, SimilarTicketsConfirmDialog } from '@/components/tickets/SimilarTicketsBanner';
import { OrderResultDialog } from '@/components/tickets/OrderActionViews';
import { SplitterCombobox } from '@/components/tickets/SplitterCombobox';
import { SELECTABLE_ORDER_SUBCATEGORIES, UTAC_SUBCATEGORIES, CHECK_SUBCATEGORIES, MOVE_FTU_REASONS } from '@uniticket/shared';
import type { SimilarTicketItem, SplitterItem, SplitterOption, OrderActionDto } from '@uniticket/shared';
import type { DmpOrderResponse, DmpAddressResponse } from '@uniticket/shared';

/**
 * Unifiber UTAC format: 18 digits starting with the 5430002977 allocation prefix.
 * Used ONLY for a soft advisory hint on the optional UTAC Swap fields — it never blocks
 * submission, and the server deliberately applies no format validation (see Order-Ticket-Rules).
 */
const UTAC_PATTERN = /^5430002977\d{8}$/;

export function OpenOrderWizard() {
  const { t } = useTranslation(['tickets', 'common']);
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [orderSubcategory, setOrderSubcategory] = useState('');
  const [jvid, setJvid] = useState('');
  const [dmpLoaded, setDmpLoaded] = useState(false);
  const [dmpData, setDmpData] = useState<DmpOrderResponse | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [similarResults, setSimilarResults] = useState<SimilarTicketItem[]>([]);
  const [showSimilarConfirm, setShowSimilarConfirm] = useState(false);
  // Patch/depatch order-automation result — when present, show a result dialog before navigating.
  const [orderResult, setOrderResult] = useState<{ orderAction: OrderActionDto; ticketNumber: string; ticketId: string } | null>(null);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('NORMAL');

  // Customer contact
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  // Distance fields
  const [metersPublic, setMetersPublic] = useState('');
  const [metersPrivate, setMetersPrivate] = useState('');
  const [metersIndoor, setMetersIndoor] = useState('');

  // Address search state
  const [searchStreet, setSearchStreet] = useState('');
  const [searchPostalCode, setSearchPostalCode] = useState('');
  const [searchHouseNumber, setSearchHouseNumber] = useState('');
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [addressSearchResults, setAddressSearchResults] = useState<DmpAddressResponse[]>([]);

  // DMP address fields (15 fields — excludes volumeMdu and existingBoxes)
  const [addressData, setAddressData] = useState<DmpAddressResponse | null>(null);
  const [addressFetched, setAddressFetched] = useState(false);
  const [addressAccordionOpen, setAddressAccordionOpen] = useState(true);

  // Group 1 — Identifiers (read-only)
  const [belmapAddressId, setBelmapAddressId] = useState('');
  const [belmapBuildingId, setBelmapBuildingId] = useState('');

  // Group 2 — Address (editable)
  const [streetName, setStreetName] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [houseNumberExt, setHouseNumberExt] = useState('');
  const [busNumber, setBusNumber] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [city, setCity] = useState('');

  // Group 3 — Coordinates (read-only)
  const [xLambert, setXLambert] = useState('');
  const [yLambert, setYLambert] = useState('');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');

  // Group 4 — Building (editable)
  const [dwellingUnitType, setDwellingUnitType] = useState('');
  const [addressSource, setAddressSource] = useState('');

  // Collapsible sections state
  const [distanceOpen, setDistanceOpen] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);

  // UTAC input (for MANUAL_DEPATCH, MOVE_FTU)
  const [utacInput, setUtacInput] = useState('');

  // Eligibility check state
  const [eligibilityPassed, setEligibilityPassed] = useState(false);
  const [eligibilityError, setEligibilityError] = useState('');
  const [eligibilityChecking, setEligibilityChecking] = useState(false);

  // Order reference fields
  const [operatorOrderRef, setOperatorOrderRef] = useState('');
  const [customerNumber, setCustomerNumber] = useState('');

  // Check filter extra fields
  const [customerReference, setCustomerReference] = useState('');
  const [moveReason, setMoveReason] = useState('');
  const [splitterResults, setSplitterResults] = useState<SplitterItem[]>([]);
  const [splitterLoading, setSplitterLoading] = useState(false);
  const [splitterQuery, setSplitterQuery] = useState('');

  // UTAC Swap fields (UTAC_SWAP only) — both optional, free text
  const [utacSwap1, setUtacSwap1] = useState('');
  const [utacSwap2, setUtacSwap2] = useState('');

  // Splitter Swap fields (SPLITTER_SWAP only) — both MANDATORY.
  // `from` is pre-filled from what DMP documents for the JVID, `to` from the siblings of that
  // reference; typing in either box searches the operator's full splitter list.
  const [splitterSwapFrom, setSplitterSwapFrom] = useState('');
  const [splitterSwapTo, setSplitterSwapTo] = useState('');
  const [documentedSplitters, setDocumentedSplitters] = useState<SplitterOption[]>([]);
  const [siblingSplitters, setSiblingSplitters] = useState<SplitterOption[]>([]);
  const [swapPrefillLoading, setSwapPrefillLoading] = useState(false);
  // Search state is kept PER FIELD: both boxes query the same endpoint, but sharing one result
  // array would let the "to" field's hits show up under "from" (and vice versa) once both have
  // been typed in, including a spurious "not found in DMP" hint on the other field's value.
  const [fromSearchResults, setFromSearchResults] = useState<SplitterOption[]>([]);
  const [toSearchResults, setToSearchResults] = useState<SplitterOption[]>([]);
  const [fromSearching, setFromSearching] = useState(false);
  const [toSearching, setToSearching] = useState(false);
  // The JVID the pre-filled lists belong to — guards the prefetch and detects a JVID change.
  // A ref, not state, on purpose: writing it must not re-run the prefetch effect, which would
  // fire the effect's cleanup and cancel the very request it just started.
  const swapLoadedJvid = useRef('');
  const swapSearchSeq = useRef<{ from: string; to: string }>({ from: '', to: '' });

  // Derived helpers
  const usesUtac = (UTAC_SUBCATEGORIES as readonly string[]).includes(orderSubcategory);
  const needsCheck = (CHECK_SUBCATEGORIES as readonly string[]).includes(orderSubcategory);

  // Customer email capture (files + pasted text) — forwarded after ticket create
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [pastedEmail, setPastedEmail] = useState('');

  /** Populate 15 address field states from a DmpAddressResponse */
  function populateAddressFields(d: DmpAddressResponse) {
    setBelmapAddressId(d.belmapAddressId != null ? String(d.belmapAddressId) : '');
    setBelmapBuildingId(d.belmapBuildingId != null ? String(d.belmapBuildingId) : '');
    setStreetName(d.streetName ?? '');
    setHouseNumber(d.houseNumber ?? '');
    setHouseNumberExt(d.houseNumberExtension ?? '');
    setBusNumber(d.busNumber ?? '');
    setPostalCode(d.postalCode ?? '');
    setMunicipality(d.municipality ?? '');
    setCity(d.city ?? '');
    setXLambert(d.x != null ? String(d.x) : '');
    setYLambert(d.y != null ? String(d.y) : '');
    setLat(d.lat != null ? String(d.lat) : '');
    setLon(d.lon != null ? String(d.lon) : '');
    setDwellingUnitType(d.dwellingUnitType ?? '');
    setAddressSource(d.addressSource ?? '');
    setAddressData(d);
    setAddressFetched(true);
    setAddressAccordionOpen(true);
  }

  /** Reset address fields */
  function clearAddressFields() {
    setBelmapAddressId('');
    setBelmapBuildingId('');
    setStreetName('');
    setHouseNumber('');
    setHouseNumberExt('');
    setBusNumber('');
    setPostalCode('');
    setMunicipality('');
    setCity('');
    setXLambert('');
    setYLambert('');
    setLat('');
    setLon('');
    setDwellingUnitType('');
    setAddressSource('');
    setAddressData(null);
    setAddressFetched(false);
    setAddressAccordionOpen(false);
  }

  async function handleJVIDLookup() {
    if (jvid.length < 8) return;
    setLookingUp(true);
    clearAddressFields();
    setMetersPublic('');
    setMetersPrivate('');
    setMetersIndoor('');
    setEligibilityPassed(false);
    setEligibilityError('');
    try {
      // Fetch order and address data in parallel
      const [orderResult, addressResult] = await Promise.allSettled([
        fetchDmpOrder(jvid),
        fetchDmpAddress(jvid),
      ]);

      if (orderResult.status === 'fulfilled') {
        const order = orderResult.value.data;
        setDmpData(order);
        setDmpLoaded(true);
        // Auto-populate distance fields from DMP
        if (order.metersPublic != null) setMetersPublic(String(order.metersPublic));
        if (order.metersPrivate != null) setMetersPrivate(String(order.metersPrivate));
        if (order.metersIndoor != null) setMetersIndoor(String(order.metersIndoor));
      } else {
        toast.error(t('tickets:openOrder.orderNotFound'));
        setDmpLoaded(false);
        setDmpData(null);
      }

      if (addressResult.status === 'fulfilled') {
        populateAddressFields(addressResult.value.data);
      }
      // Address fetch failing silently is OK — address data is supplementary for open orders

      // Run eligibility check for subcategories that need it (QUOTE, MANUAL_PATCH)
      if (needsCheck && !usesUtac) {
        await runEligibilityCheck(jvid, 'jvid');
      }
    } finally {
      setLookingUp(false);
    }
  }

  async function handleAddressSearch() {
    if (!searchStreet && !searchPostalCode && !searchHouseNumber) return;
    setSearchingAddress(true);
    setAddressSearchResults([]);
    try {
      const res = await searchDmpAddresses({
        street: searchStreet || undefined,
        postalCode: searchPostalCode || undefined,
        houseNumber: searchHouseNumber || undefined,
      });
      setAddressSearchResults(res.data.addresses);
      if (res.data.addresses.length === 0) {
        toast.info(t('tickets:openOrder.noAddresses'));
      }
    } catch (err: any) {
      // ApiError has .status (HTTP status code)
      if (err?.status === 422) {
        toast.warning(t('tickets:openOrder.tooManyResults'));
      } else {
        toast.error(t('tickets:openOrder.addressSearchFailed'));
      }
    } finally {
      setSearchingAddress(false);
    }
  }

  function handleSearchAddressSelect(address: DmpAddressResponse) {
    setJvid(String(address.jvid));
    setDmpLoaded(false);
    setDmpData(null);
    // Populate address fields directly from the selected address
    populateAddressFields(address);
    // Auto-lookup orders for the selected JVID
    setLookingUp(true);
    fetchDmpOrder(String(address.jvid))
      .then((res) => {
        const order = res.data;
        setDmpData(order);
        setDmpLoaded(true);
        if (order.metersPublic != null) setMetersPublic(String(order.metersPublic));
        if (order.metersPrivate != null) setMetersPrivate(String(order.metersPrivate));
        if (order.metersIndoor != null) setMetersIndoor(String(order.metersIndoor));
      })
      .catch(() => {
        toast.info(t('tickets:openOrder.noOrderForAddress'));
        // Mark as "loaded" so user can proceed even without order data
        setDmpLoaded(true);
      })
      .finally(() => setLookingUp(false));
  }

  async function handleUTACLookup() {
    if (utacInput.length < 3) return;
    setLookingUp(true);
    clearAddressFields();
    setEligibilityPassed(false);
    setEligibilityError('');
    try {
      const result = await fetchDmpAddressByUtac(utacInput);
      const data = result.data;
      setJvid(String(data.jvid));
      populateAddressFields(data);
      setDmpLoaded(true);
      // Try to fetch order data for the resolved JVID
      try {
        const orderResult = await fetchDmpOrder(String(data.jvid));
        setDmpData(orderResult.data);
      } catch {
        // No order — OK for UTAC-based subcategories
      }
      // Run eligibility check
      await runEligibilityCheck(utacInput, 'utac');
    } catch {
      toast.error(t('tickets:openOrder.utacNotFound'));
      setDmpLoaded(false);
    } finally {
      setLookingUp(false);
    }
  }

  async function runEligibilityCheck(identifier: string, mode: 'jvid' | 'utac') {
    setEligibilityChecking(true);
    setEligibilityError('');
    setEligibilityPassed(false);
    try {
      const result = await checkEligibility(identifier, orderSubcategory, mode);
      if (result.data.eligible) {
        setEligibilityPassed(true);
      } else {
        setEligibilityError(result.data.reason || 'eligibilityFailed');
      }
    } catch {
      setEligibilityError('eligibilityFailed');
    } finally {
      setEligibilityChecking(false);
    }
  }

  useEffect(() => {
    if (step === 2 && orderSubcategory === 'MANUAL_PATCH' && splitterResults.length === 0 && !customerReference) {
      loadSplitters();
    }
  }, [step, orderSubcategory]);

  async function loadSplitters(query?: string) {
    setSplitterLoading(true);
    try {
      const result = await searchSplitters(query && query.length >= 2 ? query : '', 50);
      setSplitterResults(result.data);
    } catch {
      setSplitterResults([]);
    } finally {
      setSplitterLoading(false);
    }
  }

  function handleSplitterSearch(query: string) {
    setSplitterQuery(query);
    setCustomerReference('');
    loadSplitters(query);
  }

  // ── Splitter Swap ─────────────────────────────────────────────
  // Pre-fill both dropdowns on entering the details step. The JVID is guaranteed to be set by
  // then: step 1 only lets you through once a DMP order or address lookup succeeded.
  useEffect(() => {
    if (step !== 2 || orderSubcategory !== 'SPLITTER_SWAP' || !jvid) return;
    // Keyed on the JVID, not on "have we loaded anything": going back to step 1, changing the
    // JVID and returning must re-fetch, otherwise both dropdowns would still offer the previous
    // address's splitters — and a picked value from the old address would silently stand.
    if (swapLoadedJvid.current === jvid) return;

    const requestedJvid = jvid;
    swapLoadedJvid.current = requestedJvid;
    setSplitterSwapFrom('');
    setSplitterSwapTo('');
    setFromSearchResults([]);
    setToSearchResults([]);

    setSwapPrefillLoading(true);
    Promise.allSettled([fetchDocumentedSplitters(requestedJvid), searchSwapSplitters(requestedJvid, '')])
      .then(([documented, siblings]) => {
        // Staleness is keyed on the JVID rather than on an effect-cleanup flag: stepping back to
        // step 1 and forward again must NOT discard an in-flight response, or the lists would
        // stay empty (the guard above already considers this JVID loaded) with the spinner stuck.
        if (swapLoadedJvid.current !== requestedJvid) return;
        const documentedRows = documented.status === 'fulfilled' ? documented.value.data : [];
        setDocumentedSplitters(documentedRows);
        setSiblingSplitters(siblings.status === 'fulfilled' ? siblings.value.data : []);
        // One documented splitter is the common case (a single fiber) — pre-select it so the
        // user only has to pick the correction.
        if (documentedRows.length === 1) setSplitterSwapFrom(documentedRows[0].customerReference);
      })
      .finally(() => {
        if (swapLoadedJvid.current === requestedJvid) setSwapPrefillLoading(false);
      });
  }, [step, orderSubcategory, jvid]);

  /**
   * Typed search for one swap field — hits the operator's full splitter list, so either box can
   * reach any splitter when DMP's suggestion for the JVID is missing or wrong. The seq ref drops
   * out-of-order responses so a slow earlier request can't overwrite a newer one's results.
   */
  async function handleSwapSearch(field: 'from' | 'to', query: string) {
    const setResults = field === 'from' ? setFromSearchResults : setToSearchResults;
    const setSearching = field === 'from' ? setFromSearching : setToSearching;

    swapSearchSeq.current[field] = query;
    if (!jvid || !query) {
      setResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    try {
      const result = await searchSwapSplitters(jvid, query);
      if (swapSearchSeq.current[field] !== query) return;
      setResults(result.data);
    } catch {
      if (swapSearchSeq.current[field] === query) setResults([]);
    } finally {
      if (swapSearchSeq.current[field] === query) setSearching(false);
    }
  }

  function handleSubmitClick() {
    if (!subject.trim()) { toast.error(t('common:validation.subjectRequired')); return; }
    if (similarResults.length > 0 && !showSimilarConfirm) {
      setShowSimilarConfirm(true);
      return;
    }
    doSubmit();
  }

  async function doSubmit() {
    setShowSimilarConfirm(false);
    setSubmitting(true);
    try {
      const result = await createTicketWithExtras({
        ticketType: 'OPEN_ORDER',
        subject: subject.trim(),
        description: description.trim() || undefined,
        priority,
        orderSubcategory: orderSubcategory || undefined,
        jvid: jvid ? Number(jvid) : undefined,
        dmpOrderId: dmpData?.orderId != null ? String(dmpData.orderId) : undefined,
        orderType: dmpData?.orderType ?? undefined,
        orderDmpSnapshot: (dmpData as unknown as Record<string, unknown>) ?? undefined,
        // Customer contact
        contactFirstName: contactFirstName.trim() || undefined,
        contactLastName: contactLastName.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        // Distance — only sent when the subcategory actually asks about distance.
        // DMP auto-populates these on JVID lookup; we drop them here otherwise to avoid noise.
        metersPublic: orderSubcategory === 'DISTANCE_INQUIRY' && metersPublic ? Number(metersPublic) : undefined,
        metersPrivate: orderSubcategory === 'DISTANCE_INQUIRY' && metersPrivate ? Number(metersPrivate) : undefined,
        metersIndoor: orderSubcategory === 'DISTANCE_INQUIRY' && metersIndoor ? Number(metersIndoor) : undefined,
        // Check filter extra fields
        utac: usesUtac && utacInput ? utacInput : undefined,
        customerReference: orderSubcategory === 'MANUAL_PATCH' && customerReference ? customerReference : undefined,
        moveReason: orderSubcategory === 'MOVE_FTU' && moveReason ? moveReason : undefined,
        // UTAC Swap fields — only sent for UTAC_SWAP
        utacSwap1: orderSubcategory === 'UTAC_SWAP' && utacSwap1.trim() ? utacSwap1.trim() : undefined,
        utacSwap2: orderSubcategory === 'UTAC_SWAP' && utacSwap2.trim() ? utacSwap2.trim() : undefined,
        // Splitter Swap fields — only sent for SPLITTER_SWAP (the server rejects the ticket if
        // either is missing, so the Next gate below must stay in sync)
        splitterSwapFrom: orderSubcategory === 'SPLITTER_SWAP' && splitterSwapFrom.trim() ? splitterSwapFrom.trim() : undefined,
        splitterSwapTo: orderSubcategory === 'SPLITTER_SWAP' && splitterSwapTo.trim() ? splitterSwapTo.trim() : undefined,
        // Order reference fields
        operatorOrderRef: operatorOrderRef.trim() || undefined,
        customerNumber: customerNumber.trim() || undefined,
        // Address fields (15 fields)
        ...(addressFetched ? {
          belmapAddressId: belmapAddressId ? Number(belmapAddressId) : undefined,
          belmapBuildingId: belmapBuildingId ? Number(belmapBuildingId) : undefined,
          addrStreet: streetName || undefined,
          addrHouseNumber: houseNumber || undefined,
          addrHouseNumberExt: houseNumberExt || undefined,
          addrBusNumber: busNumber || undefined,
          addrPostalCode: postalCode || undefined,
          addrMunicipality: municipality || undefined,
          addrCity: city || undefined,
          addrX: xLambert ? Number(xLambert) : undefined,
          addrY: yLambert ? Number(yLambert) : undefined,
          addrLat: lat ? Number(lat) : undefined,
          addrLon: lon ? Number(lon) : undefined,
          addrDwellingUnitType: dwellingUnitType || undefined,
          addrAddressSource: addressSource || undefined,
          addrDmpFetched: true,
          addrDmpSnapshot: (addressData as unknown as Record<string, unknown>) ?? undefined,
        } : {}),
      }, attachedFiles, pastedEmail);
      toast.success(t('tickets:create.created', { number: result.ticket.ticketNumber }));
      if (result.uploadErrors.length > 0) {
        toast.warning(t('tickets:customerEmail.someUploadsFailed', {
          count: result.uploadErrors.length,
          names: result.uploadErrors.map((e) => e.fileName).join(', '),
        }));
      }
      if (result.textAttachmentFailed) {
        toast.warning(t('tickets:customerEmail.textAttachmentFailed'));
      }
      // Patch/depatch tickets trigger an Ordering-API call — surface its result in a dialog
      // before navigating (the ticket is always created regardless of the order outcome).
      if (result.ticket.orderAction) {
        setOrderResult({
          orderAction: result.ticket.orderAction,
          ticketNumber: result.ticket.ticketNumber,
          ticketId: result.ticket.id,
        });
        return;
      }
      navigate(`/tickets/${result.ticket.id}`);
    } catch (err: any) {
      toast.error(err?.message || t('tickets:create.createError'));
    } finally {
      setSubmitting(false);
    }
  }

  const wizardSteps = [
    t('tickets:create.steps.subcategory'),
    usesUtac ? t('tickets:openOrder.utacLookup') : t('tickets:create.steps.jvidLookup'),
    t('tickets:create.steps.details'),
    t('tickets:create.steps.review'),
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <WizardStepper steps={wizardSteps} currentStep={step} />

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 mt-8">
        {/* Step 0: Subcategory Selection */}
        {step === 0 && (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">{t('tickets:openOrder.subcategoryTitle')}</h2>
            <p className="text-sm text-slate-500 mb-6">{t('tickets:openOrder.subcategoryHint')}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Retired subcategories (MOVE_FTU) are excluded by SELECTABLE_ORDER_SUBCATEGORIES,
                  which also drives the ticket list's Category filter — one list, so the two
                  pickers can never drift. Still accepted by the backend and rendered on existing
                  tickets for backward-compat. */}
              {SELECTABLE_ORDER_SUBCATEGORIES.map((sub) => (
                <button
                  key={sub}
                  onClick={() => {
                    const newSub = sub === orderSubcategory ? '' : sub;
                    setOrderSubcategory(newSub);
                    setEligibilityPassed(false);
                    setEligibilityError('');
                    setCustomerReference('');
                    setMoveReason('');
                    setSplitterQuery('');
                    setSplitterResults([]);
                    setUtacInput('');
                    setUtacSwap1('');
                    setUtacSwap2('');
                    setSplitterSwapFrom('');
                    setSplitterSwapTo('');
                    setDocumentedSplitters([]);
                    setSiblingSplitters([]);
                    setFromSearchResults([]);
                    setToSearchResults([]);
                    swapLoadedJvid.current = '';
                    setJvid('');
                    setDmpLoaded(false);
                    setDmpData(null);
                    clearAddressFields();
                  }}
                  className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                    orderSubcategory === sub
                      ? 'border-brand-rose bg-brand-rose/5 ring-2 ring-brand-rose/20'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <p className="text-sm font-semibold text-slate-900">
                    {t(`common:orderSubcategory.${sub}`)}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: JVID / UTAC Lookup */}
        {step === 1 && (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">
              {usesUtac ? t('tickets:openOrder.findByUtac') : t('tickets:openOrder.findOrder')}
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              {usesUtac ? t('tickets:openOrder.findByUtacHint') : t('tickets:openOrder.findOrderHint')}
            </p>

            <div className="max-w-md">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {usesUtac ? t('common:fields.utac') : t('common:fields.jvid')} <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                {usesUtac ? (
                  <input
                    type="text"
                    value={utacInput}
                    onChange={(e) => { setUtacInput(e.target.value); setDmpLoaded(false); clearAddressFields(); setEligibilityPassed(false); setEligibilityError(''); }}
                    placeholder={t('tickets:openOrder.utacPlaceholder')}
                    className="flex-1 px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono"
                  />
                ) : (
                  <input
                    type="text"
                    value={jvid}
                    onChange={(e) => { setJvid(e.target.value); setDmpLoaded(false); clearAddressFields(); setEligibilityPassed(false); setEligibilityError(''); }}
                    placeholder={t('tickets:openOrder.jvidPlaceholder')}
                    className="flex-1 px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono"
                  />
                )}
                <button
                  onClick={usesUtac ? handleUTACLookup : handleJVIDLookup}
                  disabled={usesUtac ? (utacInput.length < 3 || lookingUp) : (jvid.length < 8 || lookingUp)}
                  className="px-4 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  {lookingUp ? t('tickets:openOrder.lookingUp') : t('tickets:openOrder.lookup')}
                </button>
              </div>
            </div>

            {/* Eligibility Check Result — shown right after lookup area */}
            {needsCheck && !lookingUp && (dmpLoaded || addressFetched) && (
              <div className="mt-4 max-w-md">
                {eligibilityChecking && (
                  <div className="flex items-center gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl">
                    <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                    <span className="text-sm text-blue-700">{t('tickets:openOrder.eligibilityChecking')}</span>
                  </div>
                )}
                {eligibilityPassed && (
                  <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800">{t('tickets:openOrder.eligibilityPassed')}</span>
                  </div>
                )}
                {eligibilityError && !eligibilityChecking && (
                  <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-sm font-medium text-red-800">{t('tickets:openOrder.eligibilityFailed')}</p>
                    <p className="text-xs text-red-600 mt-1">{t(`tickets:openOrder.${eligibilityError}`)}</p>
                  </div>
                )}
              </div>
            )}

            {/* Divider — search by address */}
            <div className="flex items-center gap-3 mt-6 mb-4">
              <span className="flex-1 h-px bg-slate-200" />
              <span className="text-xs font-medium text-slate-400 uppercase">{t('tickets:openOrder.orSearchAddress')}</span>
              <span className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Address search inputs */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('tickets:openOrder.street')}</label>
                <input
                  type="text"
                  value={searchStreet}
                  onChange={(e) => setSearchStreet(e.target.value)}
                  placeholder={t('tickets:openOrder.streetPlaceholder')}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('tickets:openOrder.postalCode')}</label>
                <input
                  type="text"
                  value={searchPostalCode}
                  onChange={(e) => setSearchPostalCode(e.target.value)}
                  placeholder={t('tickets:openOrder.postalPlaceholder')}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('tickets:openOrder.houseNumber')}</label>
                <input
                  type="text"
                  value={searchHouseNumber}
                  onChange={(e) => setSearchHouseNumber(e.target.value)}
                  placeholder={t('tickets:openOrder.housePlaceholder')}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono"
                />
              </div>
            </div>
            <button
              onClick={handleAddressSearch}
              disabled={(!searchStreet && !searchPostalCode && !searchHouseNumber) || searchingAddress}
              className="mb-4 px-4 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-brand-purple to-brand-rose hover:from-brand-rose hover:to-brand-orange transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {searchingAddress ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />}
              {searchingAddress ? t('tickets:openOrder.searching') : t('tickets:openOrder.searchAddresses')}
            </button>

            {/* Address search results */}
            {addressSearchResults.length > 0 && (
              <div className="mb-4">
                <AddressPickerTable
                  addresses={addressSearchResults}
                  selectedJvid={jvid ? Number(jvid) : null}
                  onSelect={handleSearchAddressSelect}
                />
              </div>
            )}

            {/* DMP Data preview */}
            {dmpLoaded && dmpData && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-semibold text-green-800">{t('tickets:openOrder.orderFound')}</span>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                  <DMPPreview label={t('common:fields.orderId')} value={String(dmpData.orderId)} />
                  <DMPPreview label={t('common:fields.orderType')} value={dmpData.orderType || '—'} />
                  <DMPPreview label={t('common:fields.status')} value={dmpData.orderStatus || '—'} />
                  <DMPPreview label={t('common:fields.operator')} value={dmpData.operatorName ?? '—'} />
                  <DMPPreview label={t('common:fields.customerRef')} value={dmpData.customerRef ?? '—'} />
                  <DMPPreview label={t('common:fields.plannedDate')} value={dmpData.plannedDate ?? '—'} />
                </div>
              </div>
            )}

            {/* Collapsible Address Data accordion */}
            {addressFetched && (
              <div className="mt-4 border border-slate-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setAddressAccordionOpen(!addressAccordionOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                >
                  <span className="text-sm font-semibold text-slate-700">{t('tickets:openOrder.addressDataLabel')}</span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${addressAccordionOpen ? 'rotate-180' : ''}`} />
                </button>
                {addressAccordionOpen && (
                  <div className="px-4 py-4 space-y-5">
                    {/* Identifiers — read-only */}
                    <FieldGroupHeader title={t('tickets:openOrder.identifiers')} />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <AddrField label={t('common:fields.jvid')} value={jvid} disabled mono />
                      <AddrField label={t('tickets:openOrder.belmapAddressId')} value={belmapAddressId} disabled mono />
                      <AddrField label={t('tickets:openOrder.belmapBuildingId')} value={belmapBuildingId} disabled mono />
                    </div>

                    {/* Address — editable */}
                    <FieldGroupHeader title={t('tickets:openOrder.address')} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <AddrField label={t('tickets:openOrder.street')} value={streetName} onChange={setStreetName} />
                      </div>
                      <AddrField label={t('tickets:openOrder.houseNumber')} value={houseNumber} onChange={setHouseNumber} />
                      <AddrField label={t('tickets:openOrder.extension')} value={houseNumberExt} onChange={setHouseNumberExt} />
                      <AddrField label={t('tickets:openOrder.busNumber')} value={busNumber} onChange={setBusNumber} />
                      <AddrField label={t('tickets:openOrder.postalCode')} value={postalCode} onChange={setPostalCode} />
                      <AddrField label={t('tickets:openOrder.municipality')} value={municipality} onChange={setMunicipality} />
                      <AddrField label={t('tickets:openOrder.city')} value={city} onChange={setCity} />
                    </div>

                    {/* Coordinates — read-only */}
                    <FieldGroupHeader title={t('tickets:openOrder.coordinates')} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <AddrField label={t('tickets:openOrder.xLambert')} value={xLambert} disabled />
                      <AddrField label={t('tickets:openOrder.yLambert')} value={yLambert} disabled />
                      <AddrField label={t('tickets:openOrder.latitude')} value={lat} disabled />
                      <AddrField label={t('tickets:openOrder.longitude')} value={lon} disabled />
                    </div>

                    {/* Building — editable */}
                    <FieldGroupHeader title={t('tickets:openOrder.building')} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <AddrField label={t('tickets:openOrder.dwellingUnitType')} value={dwellingUnitType} onChange={setDwellingUnitType} />
                      <AddrField label={t('tickets:openOrder.addressSource')} value={addressSource} onChange={setAddressSource} />
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        )}

        {/* Step 2: Details + Distance + Contact */}
        {step === 2 && (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">{t('tickets:openOrder.ticketDetails')}</h2>
            <p className="text-sm text-slate-500 mb-6">{t('tickets:openOrder.ticketDetailsHint')}</p>

            <div className="space-y-4 max-w-lg">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('common:fields.subject')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t('tickets:openOrder.subjectPlaceholder')}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('common:fields.description')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('tickets:openOrder.descriptionPlaceholder')}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('common:fields.priority')}</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                >
                  <option value="LOW">{t('common:priority.LOW')}</option>
                  <option value="NORMAL">{t('common:priority.NORMAL')}</option>
                  <option value="HIGH">{t('common:priority.HIGH')}</option>
                  <option value="URGENT">{t('common:priority.URGENT')}</option>
                </select>
              </div>
            </div>

            <div className="mt-4 max-w-lg">
              <SimilarTicketsBanner
                subject={subject}
                description={description}
                jvid={jvid ? parseInt(jvid, 10) || undefined : undefined}
                dmpOrderId={dmpData?.orderId != null ? String(dmpData.orderId) : undefined}
                ticketType="OPEN_ORDER"
                orderSubcategory={orderSubcategory}
                onResultsChange={setSimilarResults}
              />
            </div>

            {/* Distance Data — collapsible, only relevant for DISTANCE_INQUIRY */}
            {orderSubcategory === 'DISTANCE_INQUIRY' && (
              <div className="mt-6 border border-slate-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setDistanceOpen(!distanceOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                >
                  <span className="text-sm font-semibold text-slate-700">{t('tickets:openOrder.distanceData')}</span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${distanceOpen ? 'rotate-180' : ''}`} />
                </button>
                {distanceOpen && (
                  <div className="px-4 py-4">
                    <p className="text-xs text-slate-500 mb-3">{t('tickets:openOrder.distanceHint')}</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-lg">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">{t('common:fields.metersPublic')}</label>
                        <input
                          type="number"
                          min="0"
                          value={metersPublic}
                          onChange={(e) => setMetersPublic(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">{t('common:fields.metersPrivate')}</label>
                        <input
                          type="number"
                          min="0"
                          value={metersPrivate}
                          onChange={(e) => setMetersPrivate(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">{t('common:fields.metersIndoor')}</label>
                        <input
                          type="number"
                          min="0"
                          value={metersIndoor}
                          onChange={(e) => setMetersIndoor(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Customer Reference — MANUAL_PATCH only */}
            {orderSubcategory === 'MANUAL_PATCH' && (
              <div className="mt-6 border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-slate-50">
                  <span className="text-sm font-semibold text-slate-700">{t('common:fields.customerReference')}</span>
                </div>
                <div className="px-4 py-4">
                  <p className="text-xs text-slate-500 mb-3">{t('tickets:openOrder.customerReferenceHint')}</p>
                  <input
                    type="text"
                    value={splitterQuery}
                    onChange={(e) => handleSplitterSearch(e.target.value)}
                    onFocus={() => { if (splitterResults.length === 0 && !customerReference) loadSplitters(); }}
                    placeholder={t('tickets:openOrder.customerReferencePlaceholder')}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono"
                  />
                  {splitterLoading && (
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>{t('tickets:openOrder.searching')}</span>
                    </div>
                  )}
                  {splitterResults.length > 0 && !customerReference && (
                    <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg">
                      {splitterResults.map((s) => (
                        <button
                          key={s.customerReference}
                          onClick={() => { setCustomerReference(s.customerReference); setSplitterQuery(s.customerReference); setSplitterResults([]); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0 font-mono"
                        >
                          {s.customerReference}
                        </button>
                      ))}
                    </div>
                  )}
                  {customerReference && (
                    <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-mono text-green-800">{customerReference}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Move Reason — MOVE_FTU only */}
            {orderSubcategory === 'MOVE_FTU' && (
              <div className="mt-6 border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-slate-50">
                  <span className="text-sm font-semibold text-slate-700">{t('common:fields.moveReason')}</span>
                </div>
                <div className="px-4 py-4">
                  <p className="text-xs text-slate-500 mb-3">{t('tickets:openOrder.moveReasonHint')}</p>
                  <select
                    value={moveReason}
                    onChange={(e) => setMoveReason(e.target.value)}
                    className="w-full max-w-xs px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                  >
                    <option value="">{t('tickets:openOrder.selectMoveReason')}</option>
                    {MOVE_FTU_REASONS.map((r) => (
                      <option key={r} value={r}>{t(`common:moveFtuReason.${r}`)}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* UTAC Swap — UTAC_SWAP only. Both fields optional (free text, never gate Next). */}
            {orderSubcategory === 'UTAC_SWAP' && (
              <div className="mt-6 border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-slate-50">
                  <span className="text-sm font-semibold text-slate-700">{t('tickets:openOrder.utacSwapTitle')}</span>
                </div>
                <div className="px-4 py-4">
                  <p className="text-xs text-slate-500 mb-3">{t('tickets:openOrder.utacSwapHint')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-lg">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t('common:fields.utacSwap1')}</label>
                      <input
                        type="text"
                        value={utacSwap1}
                        onChange={(e) => setUtacSwap1(e.target.value)}
                        maxLength={50}
                        placeholder={t('tickets:openOrder.utacPlaceholder')}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono"
                      />
                      {utacSwap1 && !UTAC_PATTERN.test(utacSwap1.trim()) && (
                        <p className="mt-1 text-xs text-amber-600">{t('tickets:openOrder.utacFormatHint')}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t('common:fields.utacSwap2')}</label>
                      <input
                        type="text"
                        value={utacSwap2}
                        onChange={(e) => setUtacSwap2(e.target.value)}
                        maxLength={50}
                        placeholder={t('tickets:openOrder.utacPlaceholder')}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono"
                      />
                      {utacSwap2 && !UTAC_PATTERN.test(utacSwap2.trim()) && (
                        <p className="mt-1 text-xs text-amber-600">{t('tickets:openOrder.utacFormatHint')}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Splitter Swap — SPLITTER_SWAP only. Both fields MANDATORY (they gate Next, and the
                server rejects the ticket without them). Free text is accepted on purpose: the
                ticket exists to report that DMP's records are wrong. */}
            {orderSubcategory === 'SPLITTER_SWAP' && (
              <div className="mt-6 border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-slate-50">
                  <span className="text-sm font-semibold text-slate-700">{t('tickets:openOrder.splitterSwapTitle')}</span>
                </div>
                <div className="px-4 py-4">
                  <p className="text-xs text-slate-500 mb-3">{t('tickets:openOrder.splitterSwapHint')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                    <SplitterCombobox
                      label={t('common:fields.splitterSwapFrom')}
                      value={splitterSwapFrom}
                      onChange={setSplitterSwapFrom}
                      prefillOptions={documentedSplitters}
                      searchOptions={fromSearchResults}
                      loading={swapPrefillLoading || fromSearching}
                      onSearch={(q) => handleSwapSearch('from', q)}
                      placeholder={t('tickets:openOrder.splitterSwapPlaceholder')}
                      hint={t('tickets:openOrder.splitterSwapFromHint')}
                      emptyHint={t('tickets:openOrder.splitterSwapNoDmpData')}
                    />
                    <SplitterCombobox
                      label={t('common:fields.splitterSwapTo')}
                      value={splitterSwapTo}
                      onChange={setSplitterSwapTo}
                      prefillOptions={siblingSplitters}
                      searchOptions={toSearchResults}
                      loading={swapPrefillLoading || toSearching}
                      onSearch={(q) => handleSwapSearch('to', q)}
                      placeholder={t('tickets:openOrder.splitterSwapPlaceholder')}
                      hint={t('tickets:openOrder.splitterSwapToHint')}
                      emptyHint={t('tickets:openOrder.splitterSwapNoDmpData')}
                    />
                  </div>
                  {splitterSwapFrom.trim() && splitterSwapFrom.trim() === splitterSwapTo.trim() && (
                    <p className="mt-3 text-xs text-amber-600">{t('tickets:openOrder.splitterSwapSameWarning')}</p>
                  )}
                </div>
              </div>
            )}

            {/* Customer email capture — paste + file attachments */}
            <div className="mt-4 max-w-2xl space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('tickets:customerEmail.label')}
                </label>
                <p className="text-xs text-slate-400 mb-2">{t('tickets:customerEmail.hint')}</p>
                <textarea
                  rows={6}
                  value={pastedEmail}
                  onChange={(e) => setPastedEmail(e.target.value)}
                  placeholder={t('tickets:customerEmail.placeholder')}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono resize-y"
                />
              </div>
              <AttachmentPicker files={attachedFiles} onChange={setAttachedFiles} disabled={submitting} />
            </div>

            {/* Order reference fields */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-lg">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">{t('tickets:openOrder.operatorOrderRef')}</label>
                <input
                  type="text"
                  value={operatorOrderRef}
                  onChange={(e) => setOperatorOrderRef(e.target.value)}
                  maxLength={50}
                  placeholder={t('tickets:openOrder.operatorOrderRefPlaceholder')}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">{t('tickets:openOrder.customerNumber')}</label>
                <input
                  type="text"
                  value={customerNumber}
                  onChange={(e) => setCustomerNumber(e.target.value)}
                  maxLength={50}
                  placeholder={t('tickets:openOrder.customerNumberPlaceholder')}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                />
              </div>
            </div>

            {/* Customer Contact — collapsible */}
            <div className="mt-4 border border-slate-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setContactOpen(!contactOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
              >
                <span className="text-sm font-semibold text-slate-700">{t('tickets:openOrder.customerContact')}</span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${contactOpen ? 'rotate-180' : ''}`} />
              </button>
              {contactOpen && (
                <div className="px-4 py-4">
                  <p className="text-xs text-slate-500 mb-3">{t('tickets:openOrder.customerContactHint')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-lg">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t('common:fields.contactFirstName')}</label>
                      <input
                        type="text"
                        value={contactFirstName}
                        onChange={(e) => setContactFirstName(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t('common:fields.contactLastName')}</label>
                      <input
                        type="text"
                        value={contactLastName}
                        onChange={(e) => setContactLastName(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t('common:fields.contactPhone')}</label>
                      <input
                        type="tel"
                        value={contactPhone}
                        onChange={(e) => setContactPhone(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t('common:fields.contactEmail')}</label>
                      <input
                        type="email"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">{t('tickets:openOrder.reviewSubmit')}</h2>
            <p className="text-sm text-slate-500 mb-6">{t('tickets:openOrder.reviewHint')}</p>

            <div className="space-y-4">
              {dmpLoaded && dmpData && (
                <ReviewSection title={t('tickets:detail.orderData')}>
                  <ReviewField label={t('common:fields.jvid')} value={jvid} />
                  <ReviewField label={t('common:fields.orderId')} value={String(dmpData.orderId)} />
                  <ReviewField label={t('common:fields.orderType')} value={dmpData.orderType || '—'} />
                  <ReviewField label={t('common:fields.status')} value={dmpData.orderStatus || '—'} />
                  <ReviewField label={t('common:fields.customerRef')} value={dmpData.customerRef ?? '—'} />
                  <ReviewField label={t('common:fields.plannedDate')} value={dmpData.plannedDate ?? '—'} />
                </ReviewSection>
              )}

              {addressFetched && (
                <ReviewSection title={t('tickets:detail.addressData')}>
                  <ReviewField label={t('common:fields.jvid')} value={jvid || '—'} />
                  <ReviewField label={t('tickets:openOrder.belmapAddressId')} value={belmapAddressId || '—'} />
                  <ReviewField label={t('tickets:openOrder.belmapBuildingId')} value={belmapBuildingId || '—'} />
                  <ReviewField label={t('tickets:openOrder.street')} value={streetName || '—'} />
                  <ReviewField label={t('tickets:openOrder.houseNumber')} value={houseNumber || '—'} />
                  <ReviewField label={t('tickets:openOrder.extension')} value={houseNumberExt || '—'} />
                  <ReviewField label={t('tickets:openOrder.busNumber')} value={busNumber || '—'} />
                  <ReviewField label={t('tickets:openOrder.postalCode')} value={postalCode || '—'} />
                  <ReviewField label={t('tickets:openOrder.municipality')} value={municipality || '—'} />
                  <ReviewField label={t('tickets:openOrder.city')} value={city || '—'} />
                  <ReviewField label={t('tickets:openOrder.latitude')} value={lat || '—'} />
                  <ReviewField label={t('tickets:openOrder.longitude')} value={lon || '—'} />
                  <ReviewField label={t('tickets:openOrder.dwellingUnitType')} value={dwellingUnitType || '—'} />
                  <ReviewField label={t('tickets:openOrder.addressSource')} value={addressSource || '—'} />
                </ReviewSection>
              )}

              {orderSubcategory === 'DISTANCE_INQUIRY' && (metersPublic || metersPrivate || metersIndoor) && (
                <ReviewSection title={t('tickets:openOrder.distanceData')}>
                  <ReviewField label={t('common:fields.metersPublic')} value={metersPublic || '—'} />
                  <ReviewField label={t('common:fields.metersPrivate')} value={metersPrivate || '—'} />
                  <ReviewField label={t('common:fields.metersIndoor')} value={metersIndoor || '—'} />
                </ReviewSection>
              )}

              {(contactFirstName || contactLastName || contactPhone || contactEmail) && (
                <ReviewSection title={t('tickets:openOrder.customerContact')}>
                  <ReviewField label={t('common:fields.contactFirstName')} value={contactFirstName || '—'} />
                  <ReviewField label={t('common:fields.contactLastName')} value={contactLastName || '—'} />
                  <ReviewField label={t('common:fields.contactPhone')} value={contactPhone || '—'} />
                  <ReviewField label={t('common:fields.contactEmail')} value={contactEmail || '—'} />
                </ReviewSection>
              )}

              <ReviewSection title={t('tickets:openOrder.ticketDetails')}>
                <ReviewField label={t('common:fields.type')} value={t('common:ticketType.OPEN_ORDER')} />
                {orderSubcategory && (
                  <ReviewField label={t('common:fields.orderSubcategory')} value={t(`common:orderSubcategory.${orderSubcategory}`)} />
                )}
                <ReviewField label={t('common:fields.jvid')} value={jvid || '—'} />
                {usesUtac && utacInput && (
                  <ReviewField label={t('common:fields.utac')} value={utacInput} />
                )}
                {orderSubcategory === 'MANUAL_PATCH' && customerReference && (
                  <ReviewField label={t('common:fields.customerReference')} value={customerReference} />
                )}
                {orderSubcategory === 'MOVE_FTU' && moveReason && (
                  <ReviewField label={t('common:fields.moveReason')} value={t(`common:moveFtuReason.${moveReason}`)} />
                )}
                {orderSubcategory === 'UTAC_SWAP' && utacSwap1 && (
                  <ReviewField label={t('common:fields.utacSwap1')} value={utacSwap1} />
                )}
                {orderSubcategory === 'UTAC_SWAP' && utacSwap2 && (
                  <ReviewField label={t('common:fields.utacSwap2')} value={utacSwap2} />
                )}
                {orderSubcategory === 'SPLITTER_SWAP' && splitterSwapFrom && (
                  <ReviewField label={t('common:fields.splitterSwapFrom')} value={splitterSwapFrom} />
                )}
                {orderSubcategory === 'SPLITTER_SWAP' && splitterSwapTo && (
                  <ReviewField label={t('common:fields.splitterSwapTo')} value={splitterSwapTo} />
                )}
                {operatorOrderRef && (
                  <ReviewField label={t('tickets:openOrder.operatorOrderRef')} value={operatorOrderRef} />
                )}
                {customerNumber && (
                  <ReviewField label={t('tickets:openOrder.customerNumber')} value={customerNumber} />
                )}
                <ReviewField label={t('common:fields.subject')} value={subject || '—'} />
                <ReviewField label={t('common:fields.priority')} value={t(`common:priority.${priority}`)} />
                <div className="col-span-2">
                  <ReviewField label={t('common:fields.description')} value={description || '—'} />
                </div>
              </ReviewSection>
            </div>
          </div>
        )}
      </div>

      {/* Navigation buttons */}
      <div className="flex items-center justify-between mt-6">
        <div>
          {step === 0 ? (
            <Link to="/tickets/new" className="px-5 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              {t('common:actions.cancel')}
            </Link>
          ) : (
            <button onClick={() => setStep(step - 1)} className="px-5 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              {t('common:actions.previous')}
            </button>
          )}
        </div>
        <div>
          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={
                (step === 0 && !orderSubcategory) ||
                (step === 1 && !dmpLoaded && !addressFetched) ||
                (step === 1 && needsCheck && !eligibilityPassed) ||
                (step === 2 && orderSubcategory === 'MANUAL_PATCH' && !customerReference) ||
                (step === 2 && orderSubcategory === 'MOVE_FTU' && !moveReason) ||
                (step === 2 && orderSubcategory === 'SPLITTER_SWAP' && (!splitterSwapFrom.trim() || !splitterSwapTo.trim()))
              }
              className="px-5 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common:actions.next')}
            </button>
          ) : (
            <button
              onClick={handleSubmitClick}
              disabled={submitting}
              className="px-5 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? t('tickets:create.creating') : t('tickets:create.submitTicket')}
            </button>
          )}
        </div>
      </div>

      {showSimilarConfirm && similarResults.length > 0 && (
        <SimilarTicketsConfirmDialog
          results={similarResults}
          onConfirm={doSubmit}
          onCancel={() => setShowSimilarConfirm(false)}
        />
      )}

      {orderResult && (
        <OrderResultDialog
          orderAction={orderResult.orderAction}
          ticketNumber={orderResult.ticketNumber}
          open
          onViewTicket={() => navigate(`/tickets/${orderResult.ticketId}`)}
        />
      )}
    </div>
  );
}

function DMPPreview({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-green-600/60">{label}</p>
      <p className="text-sm text-green-900">{value}</p>
    </div>
  );
}

function FieldGroupHeader({ title }: { title: string }) {
  return (
    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
      <span className="w-6 h-px bg-slate-200" />
      {title}
      <span className="flex-1 h-px bg-slate-200" />
    </h4>
  );
}

function AddrField({ label, value, onChange, disabled, mono }: {
  label: string; value: string; onChange?: (v: string) => void; disabled?: boolean; mono?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        readOnly={!onChange && disabled}
        disabled={disabled}
        className={`w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 ${mono ? 'font-mono' : ''} ${disabled ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
      />
    </div>
  );
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4">
      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{title}</h3>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2">{children}</div>
    </div>
  );
}

function ReviewField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{label}</p>
      <p className="text-sm text-slate-700">{value}</p>
    </div>
  );
}
